import {
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  API_DELETE,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../../api/api';
import { tokens } from '../../../global/theme/tokens';
import { updateOrCreate } from '../../special/updateOrCreate';
import Feedback from '../../special/Feedback';
import ExoAlert from '../../exo/ExoAlert';
import { ProjectAddElementButton } from '../../exo/project/ProjectAddElementButton';
import { OutOfOfficeCard, getStatusMeta } from './OutOfOfficeCard';
import { OutOfOfficeForm } from './OutOfOfficeForm';
import {
  getOutOfOfficeStatus,
  isVacationNotification,
  sortNotifications,
} from '../helpers/outOfOffice';
import { VacationSourceCard } from './VacationSourceCard';
import { useEmailClient } from '../useEmailClient';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import AddIcon from '@mui/icons-material/Add';

const API_PATH = 'absence-notifications';

// It is read through the email client context (which uses the
// `absence-notifications/vacation` shortcut) so the inbox list sees it too.

// The list is paginated; one page is enough to hold a user's notifications.
const LIST_PER_PAGE = 50;

/**
 * Out of office tab of the email settings: lists every notification on the left
 * and edits the selected one on the right. On narrow screens the two views swap
 * instead of sharing the width.
 */
export const ECOutOfOffice = ({ credentials = [] }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const isWide = useMediaQuery(theme.breakpoints.up('md'));
  const dateFormat = config?.general?.dateOutputFormat || 'DD.MM.YYYY';

  // null while loading, array afterwards
  const [notifications, setNotifications] = useState(null);
  // The vacation-driven reply lives in the email client context, not here: the
  // inbox list marks the mailboxes it covers, so both views must see the same
  // object and a toggle here has to show up there immediately.
  const {
    vacationNotification: vacation,
    setVacationNotification: setVacation,
    fetchVacationNotification,
  } = useEmailClient();

  const [isTogglingVacation, setIsTogglingVacation] = useState(false);
  // null = nothing open, { isNew: true } = create form,
  // { isVacation: true } = vacation editor, otherwise the entry
  const [selected, setSelected] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [statusFilters, setStatusFilters] = useState(['active', 'scheduled']);

  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: '',
  });

  function handleFeedback(text, type) {
    if (!text) return;
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  const fetchNotifications = useCallback(() => {
    if (!config) return;
    DBRequest({
      config,
      path: `${API_PATH}?perPage=${LIST_PER_PAGE}`,
      onResponse: (res) =>
        // The vacation entry comes back in this list too — it has its own card.
        setNotifications(
          (res.data || []).filter((entry) => !isVacationNotification(entry)),
        ),
      // Fall back to the empty state rather than an endless skeleton, but say
      // so — otherwise a failed load is indistinguishable from having none.
      onError: (error) => {
        setNotifications([]);
        handleFeedback(
          error?.message || t('Could not load the out of office notifications'),
          'error',
        );
      },
    });
  }, [config, t]);

  useEffect(() => {
    fetchNotifications();
    // Re-read on open so the tab never shows a stale copy of the setting.
    fetchVacationNotification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchNotifications]);

  // Writes go to the entry's own id — `/vacation` only reads it.
  function updateVacation({ payload, onLoading, onApplied, onFailed }) {
    if (!vacation?.id) return;

    DBRequest({
      config,
      path: `${API_PATH}/${vacation.id}`,
      method: API_PATCH({ ...payload }),
      onLoading,
      onResponse: (res) => onApplied(res?.data?.data || res?.data),
      onError: onFailed,
      onFeedback: handleFeedback,
    });
  }

  // `isEnabled` is only accepted on the vacation entry, and sending it alone
  // means flipping the switch can never overwrite the wording.
  function handleVacationToggle(isEnabled) {
    if (!vacation?.id) return;

    const previous = vacation;
    // Optimistic: the checkbox has to answer immediately.
    setVacation((prev) => ({ ...prev, isEnabled }));

    updateVacation({
      payload: { isEnabled },
      onLoading: setIsTogglingVacation,
      onApplied: (saved) => saved && setVacation(saved),
      // Put the switch back where it was — the backend did not accept it.
      onFailed: () => setVacation(previous),
    });
  }

  function handleVacationSubmit(payload) {
    updateVacation({
      payload,
      onLoading: setIsSaving,
      onApplied: (saved) =>
        setVacation((prev) => saved || { ...prev, ...payload }),
    });
  }

  const sorted = useMemo(
    () => sortNotifications(notifications || []),
    [notifications],
  );

  // How many entries sit behind each filter — shown on the pills so an empty
  // view is explained before it is opened.
  const counts = useMemo(() => {
    const next = { active: 0, scheduled: 0, expired: 0 };
    sorted.forEach((notification) => {
      next[getOutOfOfficeStatus(notification)] += 1;
    });
    return next;
  }, [sorted]);

  const visible = useMemo(
    () =>
      sorted.filter((notification) =>
        statusFilters.includes(getOutOfOfficeStatus(notification)),
      ),
    [sorted, statusFilters],
  );

  function toggleStatusFilter(status) {
    setStatusFilters((prev) =>
      prev.includes(status)
        ? prev.filter((entry) => entry !== status)
        : [...prev, status],
    );
  }

  // A just saved entry must not disappear behind the active filters.
  function revealIfHidden(notification) {
    const status = getOutOfOfficeStatus(notification);
    setStatusFilters((prev) =>
      prev.includes(status) ? prev : [...prev, status],
    );
  }

  function handleSubmit(payload) {
    const isEdit = Boolean(selected?.id);

    DBRequest({
      config,
      path: isEdit ? `${API_PATH}/${selected.id}` : API_PATH,
      // API_PATCH mutates the object it is handed, so it gets a copy.
      method: isEdit ? API_PATCH({ ...payload }) : API_POST(payload),
      onLoading: setIsSaving,
      onResponse: (res) => {
        const saved = res?.data?.data || res?.data;

        // Without an id there is nothing to merge into the list and a second
        // save would create a duplicate — reload instead.
        if (!saved?.id) {
          fetchNotifications();
          setSelected(null);
          return;
        }

        setNotifications((prev) => updateOrCreate(prev || [], saved));
        setSelected(saved);
        revealIfHidden(saved);
      },
      onFeedback: handleFeedback,
    });
  }

  function handleConfirmDelete() {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target?.id) return;

    function removeFromList() {
      setNotifications((prev) =>
        (prev || []).filter((item) => item.id !== target.id),
      );
      setSelected((prev) => (prev?.id === target.id ? null : prev));
    }

    DBRequest({
      config,
      path: `${API_PATH}/${target.id}`,
      method: API_DELETE(),
      onResponse: removeFromList,
      onFeedback: handleFeedback,
    });
  }

  const isEditorOpen = Boolean(selected);
  const showList = isWide || !isEditorOpen;
  const showEditor = isWide || isEditorOpen;

  return (
    <Box className="flex flex-col w-full h-full min-h-[420px] overflow-hidden">
      <Box className="flex items-center justify-between gap-2 pb-2">
        <Box className="flex flex-col min-w-0">
          <Typography variant="h4">{t('Out of Office')}</Typography>
          <Typography fontSize={12} className="opacity-60">
            {t(
              'Automatically answer incoming emails while you are away. Dates you insert are filled in when the reply is sent.',
            )}
          </Typography>
        </Box>
      </Box>

      {showList && (
        <VacationSourceCard
          data={vacation}
          isSelected={Boolean(selected?.isVacation)}
          isBusy={isTogglingVacation || vacation === null}
          isDisabled={vacation !== null && !vacation.id}
          onToggle={handleVacationToggle}
          onOpen={() => setSelected({ isVacation: true })}
        />
      )}

      <Box className="flex flex-1 gap-2 overflow-hidden">
        {showList && (
          <Box
            className="flex flex-col gap-1.5 overflow-y-auto pr-1"
            sx={{ width: isWide ? '290px' : '100%', flexShrink: 0 }}
          >
            <ProjectAddElementButton
              label={t('New notification')}
              addLabel={t('New notification')}
              sizePx="20px"
              className="shrink-0"
              onClick={() => setSelected({ isNew: true })}
              sx={{ fontSize: '12px' }}
            />

            {sorted.length > 0 && (
              <StatusFilterBar
                selected={statusFilters}
                counts={counts}
                onToggle={toggleStatusFilter}
                colors={colors}
              />
            )}

            {notifications === null
              ? [0, 1, 2].map((index) => (
                  <Skeleton
                    key={index}
                    variant="rectangular"
                    className="shrink-0"
                    sx={{ height: '86px', borderRadius: '0.5rem' }}
                  />
                ))
              : visible.map((notification) => (
                  <OutOfOfficeCard
                    key={notification.id}
                    data={notification}
                    mailboxes={credentials}
                    dateFormat={dateFormat}
                    isSelected={selected?.id === notification.id}
                    onSelect={setSelected}
                    onDelete={setDeleteTarget}
                  />
                ))}

            {/* Everything filtered away — offer the way back. */}
            {sorted.length > 0 && visible.length === 0 && (
              <Box className="flex flex-col items-center gap-1 py-6 text-center">
                <Typography fontSize={12} className="opacity-60">
                  {t('No notifications in this view')}
                </Typography>
                <Button size="small" onClick={() => setStatusFilters(FILTERS)}>
                  {t('Show all')}
                </Button>
              </Box>
            )}

            {notifications?.length === 0 && !isWide && (
              <EmptyHint onCreate={() => setSelected({ isNew: true })} />
            )}
          </Box>
        )}

        {isWide && (
          <Box sx={{ bgcolor: colors.selected }} className="w-[1px] shrink-0" />
        )}

        {showEditor && (
          <Box className="flex flex-1 min-w-0 overflow-hidden">
            {selected?.isVacation ? (
              // Waiting for the setting: opening an empty form here would let a
              // save wipe the stored wording.
              vacation === null ? (
                <Box className="flex items-center justify-center w-full">
                  <CircularProgress size="24px" />
                </Box>
              ) : (
                <OutOfOfficeForm
                  key="vacation"
                  startData={vacation}
                  mailboxes={credentials}
                  isSaving={isSaving}
                  // No period to edit and nothing to delete — this one is driven
                  // by the vacation records and is permanent.
                  hideDates
                  title={t('Out of office from vacations')}
                  infoText={t(
                    'The period is taken from your approved vacations, so there is nothing to schedule here. Insert the date placeholders and they are filled in for each vacation.',
                  )}
                  onSubmit={handleVacationSubmit}
                  onCancel={() => setSelected(null)}
                  onBack={!isWide ? () => setSelected(null) : undefined}
                />
              )
            ) : isEditorOpen ? (
              <OutOfOfficeForm
                key={selected.id || 'new'}
                startData={selected.isNew ? null : selected}
                mailboxes={credentials}
                dateFormat={dateFormat}
                isSaving={isSaving}
                onSubmit={handleSubmit}
                onCancel={() => setSelected(null)}
                onDelete={setDeleteTarget}
                onBack={!isWide ? () => setSelected(null) : undefined}
              />
            ) : (
              <EmptyState
                hasNotifications={Boolean(notifications?.length)}
                onCreate={() => setSelected({ isNew: true })}
                colors={colors}
              />
            )}
          </Box>
        )}
      </Box>

      <ExoAlert
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onSubmit={handleConfirmDelete}
        alert={{
          type: 'error',
          message: t(
            'Are you sure you want to delete this out of office notification?',
          ),
          submitLabel: t('Delete'),
        }}
      />
      <Feedback state={alertState} setState={setAlertState} />
    </Box>
  );
};

const FILTERS = ['active', 'scheduled', 'expired'];

/**
 * Status filter above the list. Every status is an independent toggle, and each
 * pill carries its own count plus the colour that status uses on the cards, so
 * the bar and the list read as one system.
 */
const StatusFilterBar = ({ selected, counts, onToggle, colors }) => {
  const { t } = useTranslation();

  return (
    <Box className="flex flex-wrap gap-1 shrink-0">
      {FILTERS.map((filter) => {
        const isSelected = selected.includes(filter);
        const count = counts[filter] || 0;
        const { color, label } = getStatusMeta(filter, colors);

        return (
          <Tooltip key={filter} title={isSelected ? t('Hide') : t('Show')}>
            <Box
              component="button"
              type="button"
              aria-pressed={isSelected}
              onClick={() => onToggle(filter)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '3px 9px',
                borderRadius: '9999px',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: 600,
                transition: '0.2s ease',
                color: 'inherit',
                // Switched off pills recede without disappearing.
                opacity: isSelected ? 1 : 0.55,
                bgcolor: isSelected ? color + '30' : colors.card,
                border: `1px solid ${isSelected ? color + '90' : 'transparent'}`,
                ':hover': {
                  opacity: 1,
                  bgcolor: color + '25',
                  border: `1px solid ${color}70`,
                },
              }}
            >
              <Box
                sx={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '9999px',
                  flexShrink: 0,
                  transition: '0.2s ease',
                  // Filled when on, hollow when off — readable without colour.
                  bgcolor: isSelected ? color : 'transparent',
                  border: `1px solid ${color}`,
                }}
              />
              {t(label)}
              <Box component="span" className="opacity-60">
                {count}
              </Box>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

const EmptyState = ({ hasNotifications, onCreate, colors }) => {
  const { t } = useTranslation();
  return (
    <Box className="flex flex-col items-center justify-center gap-3 w-full h-full text-center px-6">
      <Box
        className="flex items-center justify-center rounded-full"
        sx={{
          width: '72px',
          height: '72px',
          bgcolor: colors.info + '20',
          border: `1px solid ${colors.info}40`,
        }}
      >
        <BeachAccessIcon sx={{ fontSize: '34px', color: colors.info }} />
      </Box>
      <Typography variant="h5" fontWeight={600}>
        {hasNotifications
          ? t('Select a notification')
          : t('No out of office notification yet')}
      </Typography>
      <Typography fontSize={12} className="opacity-60 max-w-[380px]">
        {hasNotifications
          ? t('Pick an entry on the left to edit it, or create a new one.')
          : t(
              'Set a period, write your message and choose the mailboxes that should answer automatically.',
            )}
      </Typography>
      <Button
        onClick={onCreate}
        variant="contained"
        color="success"
        startIcon={<AddIcon />}
      >
        {t('New notification')}
      </Button>
    </Box>
  );
};

const EmptyHint = ({ onCreate }) => {
  const { t } = useTranslation();
  return (
    <Box className="flex flex-col items-center gap-2 py-6 text-center">
      <Typography fontSize={12} className="opacity-60">
        {t('No out of office notification yet')}
      </Typography>
      <Button
        onClick={onCreate}
        variant="contained"
        color="success"
        size="small"
      >
        {t('New notification')}
      </Button>
    </Box>
  );
};
